import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import styles from './MobileCard.module.css'
import downTrendIcon from '../../assets/image/dashboard-images/down-arrow 1.png'
import DipChart from '../chart/DipChart';
import MobileFeedbackWatchlistActions from '../common/MobileFeedbackWatchlistActions';
import { API } from '../../config/api';
import graph from '../../assets/image/chart.png';
import { FaCircleChevronDown } from "react-icons/fa6";

// Simple in-memory cache for API responses
const chartDataCache = new Map();

// Cache management utility
const cacheManager = {
    get: (key) => chartDataCache.get(key),
    set: (key, data, ttl = 5 * 60 * 1000) => {
        chartDataCache.set(key, data);
        // Set cache expiry
        setTimeout(() => {
            chartDataCache.delete(key);
        }, ttl);
    },
    has: (key) => chartDataCache.has(key),
    clear: () => chartDataCache.clear(),
    size: () => chartDataCache.size
};

const MobileCard = ({ data }) => {
    const [showChart, setShowChart] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Memoize logo computation - only recalculate when stock symbol changes
    const logoSrc = useMemo(() => {
        try {
            return require(`../../assets/image/stock-logo/${data.stock}.png`);
        } catch (err) {
            return require('../../assets/image/stock-logo/default.png');
        }
    }, [data.stock]);

    // Create cache key for API requests
    const cacheKey = useMemo(() => {
        return `${data.stockUid}-${data.month_range}`;
    }, [data.stockUid, data.month_range]);

    // Memoized API call function with caching
    const fetchChartData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Check cache first
            if (cacheManager.has(cacheKey)) {
                const cachedData = cacheManager.get(cacheKey);
                setChartData(cachedData);
                setShowChart(true);
                return;
            }

            // Make API call if not in cache
            const response = await axios.get(API.getChartDips, {
                params: {
                    stockUid: data.stockUid,
                    months: data.month_range
                }
            });

            // Destructure the nested data property
            const { data: chartApiData } = response.data;

            // Cache the result for future use
            cacheManager.set(cacheKey, chartApiData);

            setChartData(chartApiData);
            setShowChart(true);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            setError('Failed to load chart data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [data.stockUid, data.month_range, cacheKey]);

    // Memoized close handler
    const handleCloseChart = useCallback(() => {
        setShowChart(false);
        setChartData(null);
        setError(null);
    }, []);

    // Memoized keyboard handler
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!loading) {
                fetchChartData();
            }
        }
    }, [loading, fetchChartData]);

    return (
        <>
            <div className="">
                <div
                    className={styles.dips_card_wrapper}

                >
                    <div className={styles.dips_card_top}
                        onClick={fetchChartData}
                        style={{
                            opacity: loading ? 0.6 : 1,
                            transition: 'all 0.2s ease-in-out'
                        }}
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        aria-label={`View ${data.stock} chart`}
                        role="button"
                    >
                        <div className={styles.dips_name_section}>
                            <div className={styles.dips_logo_wrapper}>
                                <img src={logoSrc} alt={`${data.stock} logo`} />
                            </div>
                            <h4>{data.stock}</h4>
                        </div>
                        <span>
                            <img className={styles.graph_icon} src={graph} alt="Launch Graph" />
                        </span>
                    </div>
                    <div className={styles.dips_card_body}>
                        <div className={styles.threshold_section}>
                            <img src={downTrendIcon} alt="" />
                            <h4>{data.threshold}%</h4>
                        </div>
                        <div className={styles.stock_details}>
                            <div className={styles.downtrend_card_desc}>
                                <span>LTP</span>
                                <h4>{data.cmp}</h4>
                            </div>
                            <div className={styles.downtrend_card_desc}>
                                <h5>In {data.no_of_days_from_peak} Days</h5>
                            </div>
                        </div>
                    </div>
                    <div className={styles.dips_card_footer}>
                        <div></div>
                        <MobileFeedbackWatchlistActions 
                            stockData={data} 
                            styles={styles} 
                            referenceType="dips"
                            referenceUId={data.dipsUid}
                        />
                        <p><FaCircleChevronDown /></p>
                    </div>
                </div>
            </div>

            {/* Loading state */}
            {
                loading && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
                        <div className="bg-white p-4 rounded shadow-lg text-center">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p>Loading chart data...</p>
                        </div>
                    </div>
                )
            }

            {/* Error state */}
            {
                error && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
                        <div className="bg-white p-4 rounded shadow-lg text-center">
                            <div className="text-danger mb-3">
                                <i className="fas fa-exclamation-triangle fa-3x"></i>
                            </div>
                            <h5 className="text-danger mb-3">Error</h5>
                            <p className="mb-3">{error}</p>
                            <button
                                className="btn btn-primary me-2"
                                onClick={fetchChartData}
                                disabled={loading}
                            >
                                Something went wrong. Please try again.
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleCloseChart}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Chart modal */}
            {
                showChart && chartData && (
                    <DipChart
                        data={chartData}
                        logoSrc={logoSrc}
                        onClose={handleCloseChart}
                    />
                )
            }

        </>
    )
}

export default MobileCard